import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Building, Calendar, ChevronDown, Loader2, MapPin } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { useCustomerAuth } from 'context/CustomerAuthContext';
import { formatEventDateRange } from 'utility/dateFormatting';

export default function SelfServeEvents() {
    const { customerFetch } = useCustomerAuth();
    const [events, setEvents] = useState([]);
    const [loadStatus, setLoadStatus] = useState('loading');
    const [retry, setRetry] = useState(0);
    const [pastEventsOpen, setPastEventsOpen] = useState(false);
    const { i18n } = useLingui();

    useEffect(() => {
        let active = true;

        async function loadEvents() {
            setLoadStatus('loading');
            try {
                const response = await customerFetch('events');
                if (!active) return;
                if (response.status !== 200) throw new Error('Unable to load customer events');
                const data = await response.json();
                if (!Array.isArray(data?.events))
                    throw new Error('Invalid customer events response');
                if (!active) return;
                setEvents(data.events);
                setLoadStatus('ready');
            } catch {
                if (active) setLoadStatus('error');
            }
        }

        loadEvents();
        return () => {
            active = false;
        };
    }, [customerFetch, retry]);

    const loading = loadStatus === 'loading';
    const pastEvents = events.filter((event) => event.concluded === 1);
    const upcomingEvents = events.filter((event) => event.concluded === 0);

    return (
        <>
            <div className="mx-auto w-full max-w-screen-xl px-4 pb-8">
                {loading && (
                    <div role="status" className="flex items-center gap-3 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                        <Trans>Loading your events...</Trans>
                    </div>
                )}
                {loadStatus === 'error' && (
                    <div>
                        <p role="alert">
                            <Trans>Unable to load your events. Please try again.</Trans>
                        </p>
                        <Button onClick={() => setRetry((value) => value + 1)}>
                            <Trans>Try Again</Trans>
                        </Button>
                    </div>
                )}
                {loadStatus === 'ready' &&
                    (events.length === 0 ? (
                        <p role="status">
                            <Trans>No events found for your account.</Trans>
                        </p>
                    ) : (
                        <div className="space-y-8">
                            {[
                                {
                                    id: 'upcoming',
                                    title: <Trans>Your Events</Trans>,
                                    events: upcomingEvents,
                                    button: <Trans>View Your Tickets</Trans>,
                                },
                                {
                                    id: 'past',
                                    title: <Trans>Past Events</Trans>,
                                    events: pastEvents,
                                    button: <Trans>Event Concluded</Trans>,
                                },
                            ].map(
                                (group, index) =>
                                    group.events.length > 0 && (
                                        <section key={index}>
                                            <h2 className="text-2xl font-bold text-foreground">
                                                {group.id === 'upcoming' ? (
                                                    group.title
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="flex w-full items-center justify-between border-0 bg-transparent p-0 text-lg font-bold text-foreground cursor-pointer"
                                                        aria-expanded={pastEventsOpen}
                                                        aria-controls="past-events-list"
                                                        onClick={() =>
                                                            setPastEventsOpen((open) => !open)
                                                        }
                                                    >
                                                        <span>
                                                            {group.title} ({group.events.length})
                                                        </span>
                                                        <ChevronDown
                                                            className={
                                                                pastEventsOpen ? 'rotate-180' : ''
                                                            }
                                                            aria-hidden
                                                        />
                                                    </button>
                                                )}
                                            </h2>
                                            <ul
                                                id={group.id === 'past' ? 'past-events-list' : undefined}
                                                hidden={group.id === 'past' && !pastEventsOpen}
                                                className="list-none space-y-4 p-0"
                                            >
                                                {group.events.map((event) => (
                                                    <li key={event.event_id}>
                                                        <Card className="border-solid border-border">
                                                            <CardHeader className="pb-6">
                                                                <CardTitle className="text-xl md:text-2xl">
                                                                    {event.event_title}
                                                                </CardTitle>
                                                                {event.host_name && (
                                                                    <Link
                                                                        to={`/host/${event.host_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="inline-flex self-start items-center gap-2 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                                                                    >
                                                                        {event.host_name}
                                                                    </Link>
                                                                )}
                                                            </CardHeader>
                                                            <CardContent className="flex flex-col md:flex-row gap-6 justify-between">
                                                                <div className="space-y-3">
                                                                    {event.start_date && (
                                                                        <div className="flex items-center gap-2">
                                                                            <Calendar
                                                                                size={18}
                                                                                className="text-muted-foreground shrink-0"
                                                                                aria-hidden
                                                                            />
                                                                            <span className="text-sm">
                                                                                {formatEventDateRange(
                                                                                    event.start_date,
                                                                                    event.end_date ||
                                                                                        event.start_date,
                                                                                    i18n.locale,
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {event.address1 && (
                                                                        <div className="flex items-center gap-2">
                                                                            <Building
                                                                                size={18}
                                                                                className="text-muted-foreground shrink-0"
                                                                                aria-hidden
                                                                            />
                                                                            <span className="text-sm">
                                                                                {event.address1}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                    {[
                                                                        event.address2,
                                                                        event.city,
                                                                        event.prov_state,
                                                                    ].filter(Boolean).length >
                                                                        0 && (
                                                                        <div className="flex items-center gap-2">
                                                                            <MapPin
                                                                                size={18}
                                                                                className="text-muted-foreground shrink-0 mt-0.5"
                                                                                aria-hidden
                                                                            />
                                                                            <span className="text-sm text-muted-foreground">
                                                                                {[
                                                                                    event.address2,
                                                                                    event.city,
                                                                                    event.prov_state,
                                                                                ]
                                                                                    .filter(Boolean)
                                                                                    .join(', ')}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-col justify-end">
                                                                    {group.id === 'upcoming' ? (
                                                                        <Button asChild>
                                                                            <Link
                                                                                to={`/tickets/event/${event.event_id}`}
                                                                                className="no-underline"
                                                                            >
                                                                                {group.button}
                                                                            </Link>
                                                                        </Button>
                                                                    ) : (
                                                                        <Button
                                                                            variant="secondary"
                                                                            disabled
                                                                        >
                                                                            {group.button}
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </CardContent>
                                                        </Card>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    ),
                            )}
                        </div>
                    ))}
            </div>
        </>
    );
}
